import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType, StatementPeriodType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatements as getStatementsCall, getStatementsBalanceSheet } from '../../lib/store/fundamental';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import {
  BaseTable,
  ClickableDiv,
  ClickableImage,
  FinancialPeriodButtons,
  FinancialsTableRow,
  GoBackButton,
  MobileInfoBox,
  Preloader,
} from '../../components';
import Navigation from '../../components/Navigation/Navigation';
import SimpleFinancialTableRow from '../../components/SimpleFinancialTableRow/SimpleFinancialTableRow';
import { useIsMobile } from '../../hooks';
import TimeOptionsModal from '../SymbolDetails/Tabs/Ratios/Modals/TimeOptionsModal';

import styles from './BalancesheetStatement.module.scss';

type Props = {
  type: string;
  value: string;
};

const IncomeStatement = (props: ReduxProps) => {
  const { getAllStatements, getStatements, statementsData } = props;
  const isMobile = useIsMobile();

  const url = window.location.href;
  const symbol = url.split('/')[4];

  const [period, setPeriod] = useState <StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [selectedPeriod, setSelectedPeriod] = useState <number>(1);
  const [headers, setHeaders] = useState <string[]>([]);
  const [data, setData] = useState <Array<Array<number | string>>>([]);
  const [loading, setLoading] = useState(true);
  const [currentTimeOption, setCurrentTimeOption] = useState(0);
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const currentPeriod = currentTimeOption + 1;

  const tabs = [
    { text: lang.financialsAnnualOption(),
      component: <></>,
    },
    { text: lang.financialsQuarterlyOption(),
      component: <></>,
    },
  ];

  useEffect(() => {
    getAllStatements({
      symbol,
      periodType: period,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStatements({
      symbol,
      periodType: period,
    });
  }, [getStatements, symbol, period]);

  useEffect(() => {
    if (statementsData.balanceSheet.length > 0) {
      const headersArray: string[] = [];
      const totalAssetsArray: Array<number | string> = [];
      const currentAssetsArray: Array<number | string> = [];
      const cashAndShortTermInvestmentsArray: Array<number | string> = [];
      const cashAndEquivalentsArray: Array<number | string> = [];
      const shortTermInvestmentsArray: Array<number | string> = [];
      const receivablesArray: Array<number | string> = [];
      const inventoryArray: Array<number | string> = [];
      const otherCurrentAssetsArray: Array<number | string> = [];
      const longTermAssetsArray: Array<number | string> = [];
      const propertyPlantEquipmentArray: Array<number | string> = [];
      const longTermInvestvemntsArray: Array<number | string> = [];
      const goodwillAndIntangiblesAssetsArray: Array<number | string> = [];
      const goodwillArray: Array<number | string> = [];
      const intangibleAssetsArray: Array<number | string> = [];
      const taxAssetsArray: Array<number | string> = [];
      const otherLongTermAssetsArray: Array<number | string> = [];
      const otherAssetsArray: Array<number | string> = [];

      const totalLiabilitiesArray: Array<number | string> = [];
      const currentLiabilitiesArray: Array<number | string> = [];
      const accountsPayableArray: Array<number | string> = [];
      const shortTermDebtArray: Array<number | string> = [];
      const taxPayablesArray: Array<number | string> = [];
      const deferredRevenueArray: Array<number | string> = [];
      const otherCurrentLiabilitiesArray: Array<number | string> = [];
      const longTermLiabilitiesArray: Array<number | string> = [];
      const longTermDebtArray: Array<number | string> = [];
      const longTermDeferredRevenueArray: Array<number | string> = [];
      const deferredTaxLiabilitiesArray: Array<number | string> = [];
      const otherNonCurrentLiabilitiesArray: Array<number | string> = [];
      const otherLiabilitiesArray: Array<number | string> = [];

      const totalEquityArray: Array<number | string> = [];
      const commonStockArray: Array<number | string> = [];
      const preferredStockArray: Array<number | string> = [];
      const retainedEarningsArray: Array<number | string> = [];
      const accomulatedOtherComprehensiveIncomeArray: Array<number | string> = [];
      const otherEquityArray: Array<number | string> = [];

      const totalLaSEquityArray: Array<number | string> = [];
      const totalInvestmentsArray: Array<number | string> = [];
      const totalDebtArray: Array<number | string> = [];
      const netDebtArray: Array<number | string> = [];

      statementsData.balanceSheet.forEach((item) => {
        // eslint-disable-next-line no-unused-expressions
        headersArray.push(period === StatementPeriodType.ANNUAL ? item.calendarYear.toString() : `${item.period}'${item.calendarYear.toString().slice(-2)}`);
        totalAssetsArray.push(item.totalCurrentAssets);
        currentAssetsArray.push(item.totalCurrentAssets);
        cashAndShortTermInvestmentsArray.push(item.cashAndShortTermInvestments);
        cashAndEquivalentsArray.push(item.cashAndCashEquivalents);
        shortTermInvestmentsArray.push(item.shortTermInvestments);
        receivablesArray.push(item.netReceivables);
        inventoryArray.push(item.inventory);
        otherCurrentAssetsArray.push(item.otherCurrentAssets);
        longTermAssetsArray.push(item.longTermInvestments);
        propertyPlantEquipmentArray.push(item.propertyPlantEquipmentNet);
        longTermInvestvemntsArray.push(item.longTermInvestments);
        goodwillAndIntangiblesAssetsArray.push(item.goodwillAndIntangibleAssets);
        goodwillArray.push(item.goodwill);
        intangibleAssetsArray.push(item.intangibleAssets);
        taxAssetsArray.push(item.taxAssets);
        otherLongTermAssetsArray.push(item.otherNonCurrentAssets);
        otherAssetsArray.push(item.otherCurrentAssets);

        totalLiabilitiesArray.push(item.totalCurrentLiabilities);
        currentLiabilitiesArray.push(item.totalCurrentLiabilities);
        accountsPayableArray.push(item.accountPayables);
        shortTermDebtArray.push(item.shortTermDebt);
        taxPayablesArray.push(item.taxPayables);
        deferredRevenueArray.push(item.deferredRevenue);
        otherCurrentLiabilitiesArray.push(item.otherCurrentLiabilities);
        longTermLiabilitiesArray.push(item.totalNonCurrentLiabilities);
        longTermDebtArray.push(item.longTermDebt);
        longTermDeferredRevenueArray.push(item.deferredRevenueNonCurrent);
        deferredTaxLiabilitiesArray.push(item.deferredTaxLiabilitiesNonCurrent);
        otherNonCurrentLiabilitiesArray.push(item.otherNonCurrentLiabilities);
        otherLiabilitiesArray.push(item.otherNonCurrentLiabilities);

        totalEquityArray.push(item.totalStockholdersEquity);
        commonStockArray.push(item.commonStock);
        preferredStockArray.push(item.preferredStock);
        retainedEarningsArray.push(item.retainedEarnings);
        accomulatedOtherComprehensiveIncomeArray.push(item.accumulatedOtherComprehensiveIncomeLoss);
        otherEquityArray.push(item.otherTotalStockholdersEquity);

        totalLaSEquityArray.push(item.totalLiabilitiesAndStockholdersEquity);
        totalInvestmentsArray.push(item.totalInvestments);
        totalDebtArray.push(item.totalDebt);
        netDebtArray.push(item.netDebt);
      });

      !isMobile && headersArray.push(lang.typeHeader());
      totalAssetsArray.push(lang.financialsTotalAssets());
      currentAssetsArray.push(lang.financialsCurrentAssets());
      cashAndShortTermInvestmentsArray.push(lang.financialsCashAndShorttermInvestments());
      cashAndEquivalentsArray.push(lang.financialsCashAndEquivalents());
      shortTermInvestmentsArray.push(lang.financialsShortTermInvestments());
      receivablesArray.push(lang.financialsReceivables());
      inventoryArray.push(lang.financialsInventory());
      otherCurrentAssetsArray.push(lang.financialsOtherCurrentAssets());
      longTermAssetsArray.push(lang.financialsLongTermAssets());
      propertyPlantEquipmentArray.push(lang.financialsPropertyPlantEquipment());
      longTermInvestvemntsArray.push(lang.financialsLongTermInvestments());
      goodwillAndIntangiblesAssetsArray.push(lang.financialsGoodwillAndIntangibles());
      goodwillArray.push(lang.financialsGoodwill());
      intangibleAssetsArray.push(lang.financialsIntangibleAssets());
      taxAssetsArray.push(lang.financialsTaxAssets());
      otherLongTermAssetsArray.push(lang.financialsOtherLongTermAssets());
      otherAssetsArray.push(lang.financialsOtherAssets());

      totalLiabilitiesArray.push(lang.financialsTotalLiabilities());
      currentLiabilitiesArray.push(lang.financialsCurrentLiabilities());
      accountsPayableArray.push(lang.financialsAccountsPayable());
      shortTermDebtArray.push(lang.financialsShortTermDebt());
      taxPayablesArray.push(lang.financialsTaxPayables());
      deferredRevenueArray.push(lang.financialsDeferredRevenue());
      otherCurrentLiabilitiesArray.push(lang.financialsOtherCurrentLiabilities());
      longTermLiabilitiesArray.push(lang.financialsLongTermLiabilities());
      longTermDebtArray.push(lang.financialsLongTermDebt());
      longTermDeferredRevenueArray.push(lang.financialsDeferredRevenue());
      deferredTaxLiabilitiesArray.push(lang.financialsDeferredTaxLiabilities());
      otherNonCurrentLiabilitiesArray.push(lang.financialsOtherNonCurrentLiabilities());
      otherLiabilitiesArray.push(lang.financialsOtherLiabilities());

      totalEquityArray.push(lang.financialsTotalEquity());
      commonStockArray.push(lang.financialsCommonStock());
      preferredStockArray.push(lang.financialsPreferredStock());
      retainedEarningsArray.push(lang.financialsRetainedEarnings());
      accomulatedOtherComprehensiveIncomeArray.push(lang.financialsAccumulatedIncomeLoss());
      otherEquityArray.push(lang.financialsOtherStockholdersEquity());

      totalLaSEquityArray.push(lang.financialsTotalLiabilitiesAndStockholdersEquity());
      totalInvestmentsArray.push(lang.financialsTotalInvestments());
      totalDebtArray.push(lang.financialsTotalDebt());
      netDebtArray.push(lang.financialsTotalNet());

      setHeaders(headersArray.reverse());
      setData([
        totalAssetsArray.reverse(),
        currentAssetsArray.reverse(),
        cashAndShortTermInvestmentsArray.reverse(),
        cashAndEquivalentsArray.reverse(),
        shortTermInvestmentsArray.reverse(),
        receivablesArray.reverse(),
        inventoryArray.reverse(),
        otherCurrentAssetsArray.reverse(),
        longTermAssetsArray.reverse(),
        propertyPlantEquipmentArray.reverse(),
        longTermInvestvemntsArray.reverse(),
        goodwillAndIntangiblesAssetsArray.reverse(),
        goodwillArray.reverse(),
        intangibleAssetsArray.reverse(),
        taxAssetsArray.reverse(),
        otherLongTermAssetsArray.reverse(),
        otherAssetsArray.reverse(),

        totalLiabilitiesArray.reverse(),
        currentLiabilitiesArray.reverse(),
        accountsPayableArray.reverse(),
        shortTermDebtArray.reverse(),
        taxPayablesArray.reverse(),
        deferredRevenueArray.reverse(),
        otherCurrentLiabilitiesArray.reverse(),
        longTermLiabilitiesArray.reverse(),
        longTermDebtArray.reverse(),
        longTermDeferredRevenueArray.reverse(),
        deferredTaxLiabilitiesArray.reverse(),
        otherNonCurrentLiabilitiesArray.reverse(),
        otherLiabilitiesArray.reverse(),

        totalEquityArray.reverse(),
        commonStockArray.reverse(),
        preferredStockArray.reverse(),
        retainedEarningsArray.reverse(),
        accomulatedOtherComprehensiveIncomeArray.reverse(),
        otherEquityArray.reverse(),

        totalLaSEquityArray.reverse(),
        totalInvestmentsArray.reverse(),
        totalDebtArray.reverse(),
        netDebtArray.reverse(),
      ]);
    }
  // Period shouldn't be a dependency here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementsData, isMobile]);

  useEffect(() => {
    const options = headers.filter((header) => header !== lang.typeHeader() || !isMobile);
    setTimeOptions(options);
  }, [headers, isMobile]);

  useEffect(() => {
    setCurrentTimeOption(0);
  }, [period]);

  useEffect(() => {
    if (data.length > 0) {
      setLoading(false);
    }
  }, [data, setLoading]);

  const handlePeriodChange = (index: number) => {
    setPeriod(index === 0 ? StatementPeriodType.ANNUAL : StatementPeriodType.QUARTERLY);
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <Preloader isFullScreen text="" owner="balancesheet-statement" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {!isMobile && (
        <div className={styles.backButton}>
          <GoBackButton />
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.title}>
          {lang.financialsBalanceSheet()}
        </div>
      </div>
      <div className={styles.periodButtonsWrapper}>
        <FinancialPeriodButtons currentPeriod={period} setPeriod={setPeriod} />
      </div>
      <div className={classNames(styles.tableWrapper, { [styles.tableWrapperMob]: isMobile })}>
        {isMobile ? (
          <>
            <div className={styles.periodButtons}>
              <TimeOptionsModal
                options={timeOptions}
                currentOption={timeOptions[currentTimeOption]}
                onTimeChange={(index) => setCurrentTimeOption(index)}
                isDisabled={false}
                currentPeriod={period}
              />
            </div>
            <SimpleFinancialTableRow
              type="Type"
              value={timeOptions[currentTimeOption] || 'N/A'}
            />
            <div className={styles.wrapperInfo}>
              <FinancialsTableRow data={[data[0]]} currentPeriod={currentPeriod}>
                <FinancialsTableRow
                  data={data.slice(1, 8)}
                  isClickable
                  currentPeriod={currentPeriod}
                  isMaxNestLevel
                />
                <FinancialsTableRow
                  data={data.slice(8, 16)}
                  isClickable
                  currentPeriod={currentPeriod}
                  isMaxNestLevel
                />
                <FinancialsTableRow data={[data[16]]} currentPeriod={currentPeriod} />
              </FinancialsTableRow>
              <FinancialsTableRow data={[data[17]]} currentPeriod={currentPeriod}>
                <FinancialsTableRow
                  data={data.slice(18, 24)}
                  isClickable
                  currentPeriod={currentPeriod}
                  isMaxNestLevel
                />
                <FinancialsTableRow
                  data={data.slice(24, 29)}
                  isClickable
                  currentPeriod={currentPeriod}
                  isMaxNestLevel
                />
                <FinancialsTableRow data={[data[29]]} currentPeriod={currentPeriod} />
              </FinancialsTableRow>
              <FinancialsTableRow data={data.slice(30, 36)} isClickable currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[36]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[37]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[38]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[39]]} currentPeriod={currentPeriod} />
            </div>
          </>
        ) : (
          <BaseTable>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
            <FinancialsTableRow data={[data[0]]}>
              <FinancialsTableRow data={data.slice(1, 8)} isClickable />
              <div className={styles.middleLine} />
              <FinancialsTableRow data={data.slice(8, 16)} isClickable />
              <div className={styles.middleLine} />
              <FinancialsTableRow data={[data[16]]} />
            </FinancialsTableRow>
            <FinancialsTableRow data={[data[17]]} bigLine>
              <FinancialsTableRow data={data.slice(18, 24)} isClickable />
              <div className={styles.middleLine} />
              <FinancialsTableRow data={data.slice(24, 29)} isClickable />
              <div className={styles.middleLine} />
              <FinancialsTableRow data={[data[29]]} />
            </FinancialsTableRow>
            <FinancialsTableRow data={data.slice(30, 36)} isClickable bigLine />
            <FinancialsTableRow data={[data[36]]} />
            <FinancialsTableRow data={[data[37]]} />
            <FinancialsTableRow data={[data[38]]} />
            <FinancialsTableRow data={[data[39]]} />
          </BaseTable>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  statementsData: state.fundamental.statementsData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAllStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCall(params)),
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsBalanceSheet(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
    type ReduxProps = ConnectedProps<typeof connector>;

export default connector(IncomeStatement);
